import {
  SCREENING_REQUESTS_30D,
  SCREENING_REQUESTS_12M,
  MONITORING_NEW_MATCHES_30D,
  MONITORING_NEW_MATCHES_12M,
  MONITORING_MATCHED_NAMES_30D,
  MONITORING_MATCHED_NAMES_12M,
} from "../_data";
import type { ScreeningCounts } from "@/src/services/dashboard_counts_service";
import type { MonitoringTrend, MonitoringStatPoint } from "@/src/services/monitoring_service";
import type { ChartRange, ChartSeries } from "./chart-card";

const DAILY_30_START = "2026-03-24";
const DAILY_30_END = "2026-04-22";
const MONTHLY_12_START_MONTH = 5;
const MONTHLY_12_START_YEAR = 2025;
const MONTHLY_12_AXIS_START = "May 25";
const MONTHLY_12_AXIS_END = "Apr 26";

const DAILY_30_LABELS = generateDailyLabels(DAILY_30_START, 30);
const MONTHLY_12_LABELS = generateMonthlyLabels(MONTHLY_12_START_MONTH, MONTHLY_12_START_YEAR, 12);

export const SCREENING_REQUESTS_SERIES: Record<ChartRange, ChartSeries> = {
  "30d": {
    data: SCREENING_REQUESTS_30D,
    xLabels: DAILY_30_LABELS,
    xStart: DAILY_30_START,
    xEnd: DAILY_30_END,
    unit: { one: "request", other: "requests" },
  },
  "12m": {
    data: SCREENING_REQUESTS_12M,
    xLabels: MONTHLY_12_LABELS,
    xStart: MONTHLY_12_AXIS_START,
    xEnd: MONTHLY_12_AXIS_END,
    unit: { one: "request", other: "requests" },
  },
};

export const MATCHED_NAMES_SERIES: Record<ChartRange, ChartSeries> = {
  "30d": {
    data: MONITORING_MATCHED_NAMES_30D,
    xLabels: DAILY_30_LABELS,
    xStart: DAILY_30_START,
    xEnd: DAILY_30_END,
    unit: { one: "name", other: "names" },
  },
  "12m": {
    data: MONITORING_MATCHED_NAMES_12M,
    xLabels: MONTHLY_12_LABELS,
    xStart: MONTHLY_12_AXIS_START,
    xEnd: MONTHLY_12_AXIS_END,
    unit: { one: "name", other: "names" },
  },
};

export const NEW_MATCHES_SERIES: Record<ChartRange, ChartSeries> = {
  "30d": {
    data: MONITORING_NEW_MATCHES_30D,
    xLabels: DAILY_30_LABELS,
    xStart: DAILY_30_START,
    xEnd: DAILY_30_END,
    unit: { one: "match", other: "matches" },
  },
  "12m": {
    data: MONITORING_NEW_MATCHES_12M,
    xLabels: MONTHLY_12_LABELS,
    xStart: MONTHLY_12_AXIS_START,
    xEnd: MONTHLY_12_AXIS_END,
    unit: { one: "match", other: "matches" },
  },
};

function generateDailyLabels(startIso: string, count: number): string[] {
  const [y, m, d] = startIso.split("-").map(Number);
  const start = Date.UTC(y, m - 1, d);
  return Array.from({ length: count }, (_, i) => {
    const dt = new Date(start + i * 86400000);
    const yr = dt.getUTCFullYear();
    const mo = String(dt.getUTCMonth() + 1).padStart(2, "0");
    const da = String(dt.getUTCDate()).padStart(2, "0");
    return `${yr}-${mo}-${da}`;
  });
}

function generateMonthlyLabels(startMonth: number, startYear: number, count: number): string[] {
  const names = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  return Array.from({ length: count }, (_, i) => {
    const m = (startMonth - 1 + i) % 12;
    const y = startYear + Math.floor((startMonth - 1 + i) / 12);
    return `${names[m]} ${y}`;
  });
}
const SERIES_MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function monthYearLabel(ymdKey: string): string {
  const [y, m] = ymdKey.split("-").map(Number);
  return `${SERIES_MONTH_NAMES[m - 1] ?? ""} ${y}`;
}

function shortMonthYear(ymdKey: string): string {
  const [y, m] = ymdKey.split("-").map(Number);
  return `${(SERIES_MONTH_NAMES[m - 1] ?? "").slice(0, 3)} ${String(y).slice(2)}`;
}

export function buildScreeningSeries(counts: ScreeningCounts): Record<ChartRange, ChartSeries> {
  const { daily30, monthly12 } = counts;
  return {
    "30d": {
      data: daily30.map((p) => p.count),
      xLabels: daily30.map((p) => p.key),
      xStart: daily30[0]?.key ?? "",
      xEnd: daily30[daily30.length - 1]?.key ?? "",
      unit: { one: "request", other: "requests" },
    },
    "12m": {
      data: monthly12.map((p) => p.count),
      xLabels: monthly12.map((p) => monthYearLabel(p.key)),
      xStart: shortMonthYear(monthly12[0]?.key ?? ""),
      xEnd: shortMonthYear(monthly12[monthly12.length - 1]?.key ?? ""),
      unit: { one: "request", other: "requests" },
    },
  };
}

export function isScreeningSeriesEmpty(counts: ScreeningCounts): boolean {
  return counts.total30d === 0 && counts.total12m === 0;
}
export function buildMonitoringSeries(trend: MonitoringTrend): Record<ChartRange, ChartSeries> {
  const { month, year } = trend;
  return {
    "30d": {
      data: month.map((p) => p.count),
      xLabels: month.map((p) => p.label),
      xStart: month[0]?.label ?? "",
      xEnd: month[month.length - 1]?.label ?? "",
      unit: { one: "match", other: "matches" },
    },
    "12m": {
      data: year.map((p) => p.count),
      xLabels: year.map((p) => monthYearLabel(p.label)),
      xStart: shortMonthYear(year[0]?.label ?? ""),
      xEnd: shortMonthYear(year[year.length - 1]?.label ?? ""),
      unit: { one: "match", other: "matches" },
    },
  };
}
export function isMonitoringTrendEmpty(trend: MonitoringTrend): boolean {
  const sum = (pts: MonitoringStatPoint[]) => pts.reduce((acc, p) => acc + p.count, 0);
  return sum(trend.month) === 0 && sum(trend.year) === 0;
}
